import type { ModuleAccessLevel } from '../config/moduleAccessPolicy';
import { userCanOpenStaffHub } from '../config/staffModuleAccess';
import type { ModuleConfig, User } from '../types/config';
import type { PlatformModuleCatalogEntry } from '../services/api/types/modules';
import type { ModuleAccessSummary } from '../services/api/types/rbac';
import type { TenantModuleSubscriptionSummary } from '../services/api/types/super-admin';
import { friendlyModuleName } from './permissionsUi';

export function extractTenantModuleSubscriptions(payload: unknown): TenantModuleSubscriptionSummary[] {
  if (Array.isArray(payload)) return payload as TenantModuleSubscriptionSummary[];
  const body = payload as {
    subscriptions?: TenantModuleSubscriptionSummary[];
    data?: TenantModuleSubscriptionSummary[];
  };
  return body.subscriptions ?? body.data ?? [];
}

/** Known backend/UI code aliases (extend here only when DB uses a different slug). */
const MODULE_ACCESS_CODE_ALIASES: Record<string, string[]> = {
  exams: ['grades'],
  report_card: ['report_cards', 'reportcards'],
  certificate: ['certificates'],
  assignments: ['assignment'],
  fees: ['fee'],
  portal: ['student_portal', 'parent_portal'],
  academic_planning: ['holidays'],
};

function expandModuleCode(code: string): string[] {
  return [code, ...(MODULE_ACCESS_CODE_ALIASES[code] ?? [])];
}

function moduleAccessLookupCodes(moduleCode: string): string[] {
  return expandModuleCode(moduleCode);
}

function codesMatchModuleAccess(entryCode: string, candidateCode: string): boolean {
  const entryCodes = new Set(expandModuleCode(entryCode));
  return moduleAccessLookupCodes(candidateCode).some((code) => entryCodes.has(code));
}

function findModuleAccessEntry(
  moduleAccess: NonNullable<User['moduleAccess']>,
  moduleCode: string
) {
  return moduleAccess.find((module) => codesMatchModuleAccess(module.module_code, moduleCode));
}

function findModuleAccessForScreen(
  moduleAccess: NonNullable<User['moduleAccess']>,
  module: ModuleConfig
): ModuleAccessSummary | undefined {
  for (const code of moduleCodesForScreen(module)) {
    const entry = findModuleAccessEntry(moduleAccess, code);
    if (entry) return entry;
  }
  return undefined;
}

/** Keep only modules the user is allowed to open (login module_access is an allow-list). */
export function normalizeModuleAccessAllowList(
  modules: NonNullable<User['moduleAccess']>
): NonNullable<User['moduleAccess']> {
  return modules.filter(
    (entry) =>
      entry.enabled !== false &&
      typeof entry.access_level === 'string' &&
      entry.access_level !== 'none'
  );
}

/** Irregular screen id → backend module_code (prefer `settings.moduleCode` from DB catalog). */
const MODULE_CODE_BY_SCREEN_ID: Record<string, string | null> = {
  dashboard: 'dashboard',
  students: 'students',
  'session-promotion': 'enrollment',
  teachers: 'teachers',
  'teacher-portal': 'teacher_portal',
  'student-portal': 'portal',
  'class-attendance': 'class_attendance',
  staff: 'staff',
  'staff-leave': 'staff_leave',
  school: 'school',
  courses: 'courses',
  schedule: 'schedule',
  exams: 'exams',
  assignment: 'assignments',
  assignments: 'assignments',
  library: 'library',
  reportcards: 'report_card',
  'report-cards': 'report_card',
  certificates: 'certificate',
  fee: 'fees',
  fees: 'fees',
  transport: 'transport',
  hostel: 'hostel',
  canteen: 'canteen',
  inventory: 'inventory',
  procurement: 'procurement',
  permissions: 'permissions',
  communication: 'communication',
  announcements: 'announcements',
  security: 'security',
  holidays: 'academic_planning',
  admission: 'admission',
  'ai-questions': 'ai_questions',
  'parent-consent': 'parent_consent',
  'board-readiness': 'board_readiness',
  'risk-insights': 'risk_insights',
  'teaching-analytics': 'teaching_analytics',
  settings: 'settings',
  api: null,
  'super-admin': null,
};

export function moduleCodeForScreen(module: ModuleConfig): string | null {
  if (module.id === 'api' || module.id === 'super-admin') return null;
  const fromSettings = module.settings?.moduleCode;
  if (typeof fromSettings === 'string' && fromSettings.trim()) {
    return fromSettings.trim();
  }
  return MODULE_CODE_BY_SCREEN_ID[module.id] ?? module.id.replace(/-/g, '_');
}

/** All backend codes to try when matching a UI screen to DB module_access. */
export function moduleCodesForScreen(module: ModuleConfig): string[] {
  const primary = moduleCodeForScreen(module);
  const fromId = module.id.replace(/-/g, '_');
  const raw = [primary, fromId].filter((code): code is string => Boolean(code));
  const expanded: string[] = [];
  for (const code of raw) {
    for (const alias of moduleAccessLookupCodes(code)) {
      if (!expanded.includes(alias)) expanded.push(alias);
    }
  }
  return expanded;
}

export function userHasDbModuleAccess(user: User): boolean {
  return (user.moduleAccess?.length ?? 0) > 0;
}

/** Prefer backend `display_name` from login module_access for sidebar and page titles. */
export function resolveModuleDisplayName(module: ModuleConfig, user: User): string {
  if (user.moduleAccess?.length) {
    const entry = findModuleAccessForScreen(user.moduleAccess, module);
    const fromLogin = entry?.display_name?.trim();
    if (fromLogin && fromLogin !== entry?.module_code) return fromLogin;
    if (fromLogin) return friendlyModuleName(fromLogin);
  }
  return module.name;
}

export function applyBackendModuleNames(modules: ModuleConfig[], user: User): ModuleConfig[] {
  return modules.map((module) => {
    const name = resolveModuleDisplayName(module, user);
    return name === module.name ? module : { ...module, name };
  });
}

/** Apply DB-driven sidebar labels and sort order from `platform_modules`. */
export function applyBackendSidebarModules(
  modules: ModuleConfig[],
  catalog: PlatformModuleCatalogEntry[]
): ModuleConfig[] {
  if (!catalog.length) return modules;

  const byCode = new Map(catalog.map((entry) => [entry.module_code, entry]));
  const withLabels = modules.map((module) => {
    const codes = moduleCodesForScreen(module);
    const entry = codes.map((code) => byCode.get(code)).find(Boolean);
    const displayName = entry?.display_name?.trim();
    if (!displayName) return module;
    return { ...module, name: displayName };
  });

  const withIndex = withLabels.map((module, index) => ({ module, index }));
  withIndex.sort((a, b) => {
    const codesA = moduleCodesForScreen(a.module);
    const codesB = moduleCodesForScreen(b.module);
    const orderA = codesA.map((code) => byCode.get(code)?.sort_order).find((v) => v != null);
    const orderB = codesB.map((code) => byCode.get(code)?.sort_order).find((v) => v != null);
    if (orderA != null && orderB != null && orderA !== orderB) return orderA - orderB;
    if (orderA != null && orderB == null) return -1;
    if (orderA == null && orderB != null) return 1;
    return a.index - b.index;
  });

  return withIndex.map(({ module }) => module);
}

function accessLevelFromDbEntry(entry: ModuleAccessSummary): ModuleAccessLevel {
  if (entry.enabled === false || entry.access_level === 'none') return 'none';
  if (entry.access_level === 'write' && entry.supports_write) return 'write';
  if (entry.access_level === 'read' || entry.access_level === 'write') return 'read';
  return 'none';
}

/** Functional codes that mean the user can manage People / role templates (Permissions write). */
const PERMISSIONS_WRITE_CODES = [
  'role:assign',
  'role:permission:assign',
  'role:create',
  'user:create',
  'permission:create',
  'subscription:manage',
] as const;

export function userHasPermissionsManageCodes(user: Pick<User, 'permissions'>): boolean {
  const perms = user.permissions ?? [];
  return PERMISSIONS_WRITE_CODES.some((code) => perms.includes(code));
}

/**
 * DB module_access (login / users/me/module-access) is the only source of truth.
 * No module_access rows for the session → no access (never fall back to role names).
 */
export function getModuleAccessLevel(
  user: User,
  moduleCode: string,
  module?: ModuleConfig
): ModuleAccessLevel {
  if (!user.moduleAccess?.length) return 'none';
  const entry = module
    ? findModuleAccessForScreen(user.moduleAccess, module)
    : findModuleAccessEntry(user.moduleAccess, moduleCode);
  if (!entry) return 'none';
  const level = accessLevelFromDbEntry(entry);
  // `permissions` is seeded screen_only (supports_write=false), so DB never reports write.
  // Owners/admins still have role:* / user:create — treat that as write for People tabs.
  const code = (module ? moduleCodeForScreen(module) : moduleCode) ?? moduleCode;
  if (code === 'permissions' && level !== 'none' && userHasPermissionsManageCodes(user)) {
    return 'write';
  }
  return level;
}

export function canUserOpenModule(module: ModuleConfig, user: User): boolean {
  if (!module.enabled) return false;

  const moduleCode = moduleCodeForScreen(module);

  // Screens without a backend module_code (API explorer, super-admin) are platform-only.
  if (moduleCode === null) {
    return user.role === 'super_admin'
      ? module.permissions.includes('super_admin')
      : false;
  }

  // Platform super-admin never uses tenant sidebar modules.
  if (user.role === 'super_admin') return false;

  // Teachers is folded into Staff (tabs + Staff/Teachers submodules). Never show it alone.
  if (moduleCode === 'teachers' || moduleCode === 'teacher') return false;

  if (moduleCode === 'staff') {
    return userCanOpenStaffHub(user);
  }

  // Tenant DB module_access for the user's role is the allow-list — nothing else.
  return getModuleAccessLevel(user, moduleCode, module) !== 'none';
}

/** Dashboard/quick-action path first segment → screen id used by the sidebar catalog. */
const PATH_SEGMENT_TO_SCREEN_ID: Record<string, string> = {
  teachers: 'staff',
  teacher: 'staff',
  assignments: 'assignment',
  fees: 'fee',
  'report-cards': 'reportcards',
};

function isKnownAppScreenId(screenId: string): boolean {
  return Object.prototype.hasOwnProperty.call(MODULE_CODE_BY_SCREEN_ID, screenId);
}

/**
 * Whether the user may open a dashboard/sidebar target path (query string ignored).
 * Unknown / non-module links stay visible so this cannot hide unrelated features.
 * `/teachers` maps to the Staff hub (teachers is folded into Staff).
 */
export function canUserOpenPath(user: User | undefined, path: string | undefined): boolean {
  if (!path || !path.trim()) return true;
  if (!user) return true;
  const pathname = path.split('?')[0].split('#')[0].trim();
  if (!pathname || pathname === '/') return true;
  const segment = pathname.replace(/^\//, '').replace(/\/+$/, '').split('/')[0];
  if (!segment) return true;
  const screenId = PATH_SEGMENT_TO_SCREEN_ID[segment] ?? segment;
  if (!isKnownAppScreenId(screenId) && !isKnownAppScreenId(segment)) {
    return true;
  }
  const module: ModuleConfig = {
    id: screenId,
    name: screenId,
    icon: '',
    path: `/${screenId}`,
    enabled: true,
    component: '',
    permissions: [],
  };
  return canUserOpenModule(module, user);
}

/** Dev diagnostic: explain why each module is shown/hidden for a user (DB access only). */
export function explainModuleAccess(
  modules: ModuleConfig[],
  user: User
): Array<{
  id: string;
  moduleCode: string | null;
  dbLevel: ModuleAccessLevel;
  visible: boolean;
}> {
  return modules.map((module) => {
    const moduleCode = moduleCodeForScreen(module);
    const dbLevel = moduleCode ? getModuleAccessLevel(user, moduleCode, module) : 'none';
    return {
      id: module.id,
      moduleCode,
      dbLevel,
      visible: canUserOpenModule(module, user),
    };
  });
}

export function canUserWriteModule(module: ModuleConfig, user: User): boolean {
  const moduleCode = moduleCodeForScreen(module);
  if (!moduleCode) return user.role === 'super_admin';
  return getModuleAccessLevel(user, moduleCode, module) === 'write';
}

export function canUserWriteModuleCode(user: User, moduleCode: string): boolean {
  return getModuleAccessLevel(user, moduleCode) === 'write';
}

/** Grades/report-card admin (templates, rules, full exam admin) — DB write on exams. */
export function canUserManageGradesAdmin(config: ModuleConfig, user: User): boolean {
  return getModuleAccessLevel(user, 'exams', config) === 'write';
}

export function canUserRecordExamMarks(user: User): boolean {
  const level = getModuleAccessLevel(user, 'exams');
  return level === 'write' || level === 'read';
}
